import { Avatar, Button } from "@etframework/ui.components";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatDateFromNow, GetFieldType } from "../../utils/helpers";
import { useUpdateGroupVisibility } from "../../data/mutations/customFieldGroupMutation";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import useModalEvent from "../../hooks/useModalEvent";

export const FieldGroupUpdateContainer = ({
  modalRef,
  setCustomFieldGroupId,
  setIsUpdate,
  customFieldGroup,
}) => {
  const { t, i18n } = useTranslation(["CustomFieldGroup"], {
    useSuspense: false,
  });
  const queryClient = useQueryClient();
  const { ModalEvents, toggleModal } = useModalEvent();

  //Mutations
  const { mutate: updateGroupVisibility } = useUpdateGroupVisibility();

  const onVisibilityChange = () => {
    updateGroupVisibility(
      {
        visibility: {
          Id: customFieldGroup?.Id,
          Visible: true,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            "get-custom-field-group",
            customFieldGroup?.Id,
          ]);
          toast.success(t("SuccessUpdate"), { position: "bottom-left" });
        },
        onError: () => {
          toast.error(t("ErrorMsg"), { position: "bottom-left" });
        },
      }
    );
  };

  useEffect(() => {
    const handleDeleteSuccess = () => {
      modalRef.current.close();
    };

    window.addEventListener("DeleteGroupModal:Success", handleDeleteSuccess);

    return () => {
      window.removeEventListener(
        "DeleteGroupModal:Success",
        handleDeleteSuccess
      );
    };
  }, []);

  return (
    <div className="update-custom-fields row px-3">
      <div className="col-8 et-br-light-gray-1 px-4 pt-3 pb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div className="field-group-name">
            <div className="et-darkest-gray">{t("FieldGroup")}</div>
            <div className="d-flex justify-content-start align-items-center gap-3">
              <div className="fw-600 fs-16">{customFieldGroup?.Name}</div>
              {!customFieldGroup?.Visible && (
                <div className="d-flex align-items-center gap-2 br-100 bg-et-light-gray px-2">
                  <i className="fa-regular fa-eye-slash et-darkest-gray"></i>
                  <div className="fs-12">{t("Inactive")}</div>
                </div>
              )}
            </div>
          </div>

          <div className="buttons d-flex align-items-center gap-3">
            <Button
              text={customFieldGroup?.Visible ? t("Inactivate") : t("Activate")}
              className="btn-tertiary"
              iconCss={`fa-regular ${
                customFieldGroup?.Visible ? "fa-eye-slash" : "fa-eye"
              }`}
              onClick={() => {
                if (customFieldGroup?.Visible) {
                  toggleModal(ModalEvents.InactivateFieldGroupModal, {
                    groupId: customFieldGroup?.Id,
                  });
                } else {
                  onVisibilityChange();
                }
              }}
            />

            {!customFieldGroup?.Locked && (
              <>
                <div className="vertical-divider"></div>
                <Button
                  text={t("Edit")}
                  className="btn btn-primary"
                  onClick={() => {
                    setIsUpdate(false);
                    setCustomFieldGroupId(customFieldGroup?.Id);
                  }}
                />
              </>
            )}
          </div>
        </div>

        <div className="update-container bg-lightest-gray bc-bg-light-gray rounded p-3 mt-3">
          {customFieldGroup?.CustomFields?.map((customField) => {
            return (
              <div
                key={customField.Id}
                className="custom-field-item bg-white p-3 mx-3 my-2 shadow-sm rounded"
              >
                <div className="d-flex justify-content-start align-items-center gap-4">
                  <div className="field-icon">
                    <i
                      className={`${
                        GetFieldType(customField.Type).icon
                      } fs-16 et-darkest-gray`}
                    ></i>
                  </div>
                  <div>
                    <div className="field-type et-darkest-gray fs-12">
                      {t(`FieldType.${GetFieldType(customField.Type).title}`)}
                    </div>
                    <div className="field-name fw-600">{customField.Label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!customFieldGroup?.Locked ? (
        <div className="col-4 px-4 p-3">
          <div className="created-by px-4 py-3 bc-bg-lightest-gray shadow-sm rounded">
            <Avatar content={customFieldGroup?.CreateBy?.FullName} />
            <div className="fw-600 mt-2">{`${t("By")} ${
              customFieldGroup?.CreateBy?.FullName
            }`}</div>
          </div>

          <div className="created-on px-1 py-4 mt-4">
            <div className="et-darkest-gray">{t("CreatedOn")}</div>
            <div className="fw-600">
              {formatDateFromNow(
                customFieldGroup?.CreatedDate,
                i18n.language,
                "ll"
              )}
            </div>
          </div>

          {customFieldGroup?.UpdatedBy && (
            <div className="last-modified px-1 py-4">
              <div className="et-darkest-gray">{t("LastModified")}</div>
              <div className="fw-600">
                {customFieldGroup?.UpdatedBy?.FullName}
              </div>
              <div className="fw-600">
                {formatDateFromNow(
                  customFieldGroup?.UpdatedDate,
                  i18n.language,
                  "ll"
                )}
              </div>
            </div>
          )}

          <div className="py-4">
            <Button
              text={t("Delete")}
              className="btn btn-default w-auto et-dark-blue delete-btn"
              onClick={() => {
                toggleModal(ModalEvents.DeleteGroupConfirmationModal, {
                  groupId: customFieldGroup?.Id,
                  groupName: customFieldGroup?.Name,
                });
              }}
              iconCss="fa-regular fa-trash-can et-black"
            />
          </div>
        </div>
      ) : (
        <div className="col-4 p-4">
          <div className="created-by px-4 py-3 bc-bg-lightest-gray shadow-sm rounded">
            <img src="/et-logo.png" className="et-logo" />
            <div className="fw-600 mt-2">{t("ByExpertPractice")}</div>
          </div>
        </div>
      )}
    </div>
  );
};
